import React, { Component, type ReactNode, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, MemoryRouter } from 'react-router';
import { Layout } from './components/common/Layout';
import { MiniAppDisabledScreen } from './components/common/MiniAppDisabledScreen';
import { PageSkeleton } from './components/common/PageSkeleton';
import { useAppSettings } from './stores/appSettings';
import { safeLocalStorage } from './utils/storage';
import { logMiniAppEvent } from './utils/api';

// Ленивая загрузка страниц для уменьшения начального размера бандла
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const BookingsPage = lazy(() => import('./pages/BookingsPage').then(m => ({ default: m.BookingsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const StaffPage = lazy(() => import('./pages/StaffPage').then(m => ({ default: m.StaffPage })));
const OwnersPage = lazy(() => import('./pages/OwnersPage').then(m => ({ default: m.OwnersPage })));
const LogsPage = lazy(() => import('./pages/LogsPage').then(m => ({ default: m.LogsPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const AdminAIPage = lazy(() => import('./pages/AdminAIPage').then(m => ({ default: m.AdminAIPage })));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage').then(m => ({ default: m.ReviewsPage })));
const PromoPage = lazy(() => import('./pages/PromoPage').then(m => ({ default: m.PromoPage })));
const FinancePage = lazy(() => import('./pages/FinancePage').then(m => ({ default: m.FinancePage })));
const CRMPage = lazy(() => import('./pages/CRMPage').then(m => ({ default: m.CRMPage })));
const ClientBookingPage = lazy(() => import('./pages/ClientBookingPage').then(m => ({ default: m.ClientBookingPage })));
const ClientHistoryPage = lazy(() => import('./pages/ClientHistoryPage').then(m => ({ default: m.ClientHistoryPage })));
const ClientLoyaltyPage = lazy(() => import('./pages/ClientLoyaltyPage').then(m => ({ default: m.ClientLoyaltyPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })));
const SandboxPage = lazy(() => import('./pages/SandboxPage').then(m => ({ default: m.SandboxPage })));

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    try {
      const initData = typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '';
      if (initData) {
        logMiniAppEvent(
          initData,
          'error',
          `[React UI Crash] ${error.message}`,
          `Stack: ${error.stack || ''}\nComponentStack: ${errorInfo.componentStack || ''}`
        ).catch(() => {});
      }
      if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        window.Telegram.WebApp.ready();
      }
    } catch {
      /* ignore */
    }
  }

  public override render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: 'var(--tg-viewport-height, 100dvh)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          background: 'var(--tg-theme-bg-color, #ffffff)',
          color: 'var(--tg-theme-text-color, #000000)',
          fontFamily: 'system-ui, sans-serif'
        }}>
          <span style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</span>
          <h2 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>Что-то пошло не так</h2>
          <p style={{ fontSize: '13px', opacity: 0.6, marginBottom: '24px', maxWidth: '300px', lineHeight: 1.5 }}>
            Произошла непредвиденная ошибка в приложении. Мы уже работаем над её устранением.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'var(--tg-theme-button-color, #007AFF)',
              color: 'var(--tg-theme-button-text-color, #ffffff)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Перезагрузить приложение 🔄
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function HomeWrapper(): React.JSX.Element {
  const { isClientMode } = useAppSettings();
  return (
    <Suspense fallback={<PageSkeleton />}>
      {isClientMode ? <ClientBookingPage /> : <DashboardPage />}
    </Suspense>
  );
}

function SafeBrowserRouter({ children }: { children: React.ReactNode }) {
  const [useMemory, setUseMemory] = React.useState(false);
  const initialEntries = React.useMemo(() => {
    if (typeof window === 'undefined') return ['/'];
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    return [currentPath || '/'];
  }, []);

  React.useEffect(() => {
    try {
      window.history.replaceState(window.history.state, '');
    } catch (e) {
      console.warn('[Router] History API is restricted (SecurityError). Falling back to MemoryRouter:', e);
      setUseMemory(true);
    }
  }, []);

  if (useMemory) {
    return <MemoryRouter initialEntries={initialEntries}>{children}</MemoryRouter>;
  }
  return <BrowserRouter>{children}</BrowserRouter>;
}

export function App(): React.JSX.Element {
  const [tapCount, setTapCount] = React.useState(0);
  const [isAppDisabled, setIsAppDisabled] = React.useState(false);
  const [disabledBotName, setDisabledBotName] = React.useState<string | undefined>();

  React.useEffect(() => {
    const handleDisabled = (e: Event) => {
      const customEvent = e as CustomEvent<{ botName?: string }>;
      setIsAppDisabled(true);
      if (customEvent.detail?.botName) {
        setDisabledBotName(customEvent.detail.botName);
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      const initData = typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '';
      if (initData) {
        logMiniAppEvent(
          initData,
          'error',
          `[Unhandled Error] ${event.message || 'Script error'}`,
          `File: ${event.filename}:${event.lineno}:${event.colno}\nError: ${event.error?.stack || String(event.error)}`
        ).catch(() => {});
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const initData = typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '';
      if (initData) {
        const reason = event.reason;
        const msg = reason instanceof Error ? reason.message : String(reason);
        const stack = reason instanceof Error ? reason.stack : '';
        logMiniAppEvent(
          initData,
          'error',
          `[Unhandled Rejection] ${msg}`,
          stack || 'Unhandled Promise Rejection'
        ).catch(() => {});
      }
    };

    window.addEventListener('miniapp:disabled', handleDisabled);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('miniapp:disabled', handleDisabled);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const handleVersionClick = () => {
    const nextCount = tapCount + 1;
    setTapCount(nextCount);
    if (nextCount >= 5) {
      setTapCount(0);
      try {
        safeLocalStorage.setItem('eruda', 'true');
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/eruda';
        document.head.appendChild(script);
        script.onload = () => {
          const win = window as any;
          if (win.eruda) {
            win.eruda.init();
            alert('🛠️ Консоль отладки Eruda активирована!');
          }
        };
      } catch (err) {
        console.error('Failed to load Eruda:', err);
      }
    }
  };

  if (isAppDisabled) {
    return (
      <MiniAppDisabledScreen 
        botName={disabledBotName} 
        onRetry={() => {
          setIsAppDisabled(false);
          window.location.reload();
        }} 
      />
    );
  }

  return (
    <ErrorBoundary>
      <SafeBrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<HomeWrapper />} />
              <Route path="app" element={<HomeWrapper />} />
              <Route path="app/*" element={<HomeWrapper />} />
              <Route path="index.html" element={<HomeWrapper />} />
              <Route path="bookings" element={<BookingsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="staff" element={<StaffPage />} />
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="crm" element={<CRMPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="accept-invite/:code" element={<AcceptInvitePage />} />
              
              {/* B2C Клиентские маршруты */}
              <Route path="client-history" element={<ClientHistoryPage />} />
              <Route path="client-loyalty" element={<ClientLoyaltyPage />} />
              
              {/* Админские маршруты */}
              <Route path="owners" element={<OwnersPage />} />
              <Route path="ai-agents" element={<AdminAIPage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="promo" element={<PromoPage />} />
              <Route path="finance" element={<FinancePage />} />
              <Route path="sandbox" element={<SandboxPage />} />

              {/* Catch-all роут для обработки всех неизвестных или несовпадающих путей */}
              <Route path="*" element={<HomeWrapper />} />
            </Route>
          </Routes>
        </Suspense>
      </SafeBrowserRouter>
      <div 
        onClick={handleVersionClick}
        style={{ 
          position: 'fixed', 
          bottom: '4px', 
          left: '8px', 
          fontSize: '9px', 
          opacity: 0.4, 
          pointerEvents: 'auto', 
          cursor: 'pointer',
          zIndex: 10000, 
          fontFamily: 'system-ui, sans-serif',
          userSelect: 'none'
        }}
      >
        v1.3.0 {tapCount > 0 ? `(${tapCount}/5)` : ''}
      </div>
    </ErrorBoundary>
  );
}

export default App;
